import {SxType} from "@baseTypes/sx/sxTypes";
import {alpha, Theme} from "@mui/material/styles";

/**
 * Four states, one scale, every value taken from the theme.
 *
 *   hollow   nobody touched it     text.disabled
 *   amber    running, or mixed     warning.main
 *   green    done                  success.main
 *   red      not done              error.main
 *
 * These used to be hand-picked hex, which is the same value in the dark theme
 * and the light one and so belongs to neither - next to the blue the rest of the
 * app is built on they read as a second, competing palette. Palette tokens
 * resolve per mode, which makes these the app's own colours by definition.
 *
 * They are also tonal, never solid. A solid green square beside a solid red one
 * is two shouts at full volume, and the panel is mostly grey precisely so that a
 * 20px mark can be seen without shouting: the fill is a wash of the state
 * colour, the hairline and the digit carry it at full strength. Same information,
 * a tenth of the ink.
 *
 * Colour says state and only state - never which button does what. Red against
 * green is invisible to roughly one man in twelve, so every mark also carries a
 * glyph, except where it is too small to hold one and the name is in the tooltip.
 */
const tonal = (theme: Theme, main: string) => ({
    backgroundColor: alpha(main, theme.palette.mode === 'dark' ? 0.26 : 0.16),
    borderColor: main,
    color: main,
})

export const sx: SxType = {
    name: {
        fontSize: '12px',
        fontWeight: 600,
        color: 'text.primary',
    },
    nameSubTask: {
        fontSize: '11px',
        fontWeight: 600,
        color: 'text.primary',
    },
    fullWidth: {
        width: '100%'
    },
    description: {
        fontSize: '11px',
        lineHeight: '15px',
        color: 'text.secondary',
    },

    /**
     * Task number as plain text, not a badge: badges are taken by stop numbers,
     * and two kinds of badge side by side would be confusing.
     */
    taskNumber: {
        fontSize: '12px',
        fontWeight: 700,
        opacity: 0.55,
        flexShrink: 0,
        fontVariantNumeric: 'tabular-nums',
    },
    subTaskNumber: {
        fontSize: '10px',
        fontWeight: 700,
        opacity: 0.55,
        flexShrink: 0,
        fontVariantNumeric: 'tabular-nums',
    },

    /**
     * Side panel: one task, one block.
     *
     * The tasks used to run together on the panel's own surface - the description,
     * the strip and the subtasks of the fourth task sat flush against the title of
     * the fifth, and nothing said where one ended. The panel is the grey tray now
     * and each block comes back up to page level on top of it, which separates
     * them without drawing a single extra line.
     */
    taskCard: (theme: Theme) => ({
        width: '100%',
        boxSizing: 'border-box',
        padding: '8px 10px',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: theme.palette.divider,
        borderLeftWidth: '3px',
        borderLeftColor: theme.palette.text.disabled,
        backgroundColor: theme.palette.background.paper,
        transition: 'background-color .2s',
        '&.done': {borderLeftColor: alpha(theme.palette.success.main, 0.7)},
        '&.failed': {borderLeftColor: alpha(theme.palette.error.main, 0.7)},
        '&.running': {borderLeftColor: alpha(theme.palette.warning.main, 0.7)},
        '&.partial': {borderLeftColor: alpha(theme.palette.warning.main, 0.7)},
        '&.acting': {backgroundColor: theme.palette.action.selected},
    }),

    /** Stop card row: the state is on the dot, so the row itself stays plain. */
    taskRow: {
        width: '100%',
        minWidth: 0,
        borderRadius: '4px',
        padding: '2px 2px',
        '&.acting': {backgroundColor: 'action.selected'},
    },

    cameraIcon: {
        width: '13px',
        height: '13px',
        flexShrink: 0,
        color: 'text.secondary',
    },
    cameraIconSmall: {
        width: '12px',
        height: '12px',
        flexShrink: 0,
        color: 'text.secondary',
    },

    /** Subtask nesting is shown by a left rule, not by indentation alone. */
    subtasks: {
        paddingLeft: '8px',
        marginLeft: '4px',
        marginTop: '6px',
        borderLeftWidth: '2px',
        borderLeftStyle: 'solid',
        borderLeftColor: 'divider',
        gap: '3px',
    },
    /** The same rule in the stop cards, where every pixel of height is spent. */
    subtasksCompact: {
        paddingLeft: '6px',
        marginLeft: '6px',
        borderLeftWidth: '1px',
        borderLeftStyle: 'solid',
        borderLeftColor: 'divider',
        gap: 0,
    },

    /**
     * The task state as one mark, for the stop cards on the left. There the mark
     * carries no route number, so it can carry a glyph and stop leaning on hue.
     */
    stateDot: (theme: Theme) => ({
        position: 'relative',
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        flexShrink: 0,
        overflow: 'hidden',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        borderWidth: '1.5px',
        borderStyle: 'solid',
        borderColor: theme.palette.text.disabled,
        backgroundColor: 'transparent',
        '&.done': tonal(theme, theme.palette.success.main),
        '&.failed': tonal(theme, theme.palette.error.main),
        '&.running': tonal(theme, theme.palette.warning.main),
        // Rolled up onto a parent's line, where it is one of several. Too small
        // for a glyph, so the name and the state are in the tooltip instead.
        '&.small': {
            width: '9px',
            height: '9px',
            borderWidth: '1px',
        },
        // Partly done, and nothing left to do: half the circle is filled.
        '&.partial': {
            borderColor: theme.palette.warning.main,
            '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                bottom: 0,
                width: '100%',
                height: '50%',
                backgroundColor: alpha(theme.palette.warning.main, 0.55),
            },
        },
    }),
    stateDotIcon: {
        width: '10px',
        height: '10px',
    },

    /** Collapsed stop card: how much work is here, without listing it. */
    tasksSummary: {
        width: '100%',
        gap: '6px',
        padding: '2px 0',
        cursor: 'pointer',
        userSelect: 'none',
    },
    tasksSummaryText: {
        fontSize: '11px',
        fontWeight: 600,
        color: 'text.secondary',
    },
    tasksSummaryChevron: {
        width: '14px',
        height: '14px',
        opacity: 0.55,
        marginLeft: 'auto',
    },
}
